// rangeWrite — null-safe decomposition for set_range values grids.
//
// Prod incident (2026-07-10, "HR Dashboard"): the model stages helper
// blocks as a labels write whose values grid holds `null` where formulas
// will land, followed by a formulas-only write over those cells. The
// client reported every change applied, yet the formula cells ended up
// EMPTY — the null-holed write interacting with the overlapping formulas
// write lost data silently (host-dependent Office.js null semantics).
//
// The applier therefore never sends `null` to Office.js at all: a values
// grid is decomposed into maximal dense rectangles (consecutive rows with
// the same non-null column mask, split into contiguous column segments),
// and each rectangle is written as its own sub-range. Cells the model
// left null are simply never touched, so an overlapping formulas write
// owns them exclusively — regardless of write order or host version.

export interface DenseRun {
  // Offsets (0-based) from the grid's top-left corner.
  row: number;
  col: number;
  values: unknown[][];
}

// nonNullColumnRuns returns dense rectangles covering exactly the
// non-null cells of the grid. Empty string counts as a real value —
// only null/undefined are holes.
export function nonNullColumnRuns(grid: readonly (readonly unknown[])[]): DenseRun[] {
  const isHole = (v: unknown): boolean => v === null || v === undefined;
  const maskOf = (row: readonly unknown[]): string =>
    row.map((v) => (isHole(v) ? '0' : '1')).join('');

  const runs: DenseRun[] = [];
  let start = 0;
  while (start < grid.length) {
    const mask = maskOf(grid[start]);
    let end = start + 1;
    while (end < grid.length && maskOf(grid[end]) === mask) end++;
    // Split this row band's shared mask into contiguous column segments.
    let col = 0;
    while (col < mask.length) {
      if (mask[col] === '0') { col++; continue; }
      let colEnd = col + 1;
      while (colEnd < mask.length && mask[colEnd] === '1') colEnd++;
      runs.push({
        row: start,
        col,
        values: grid.slice(start, end).map((r) => r.slice(col, colEnd) as unknown[]),
      });
      col = colEnd;
    }
    start = end;
  }
  return runs;
}
